import { useMemo } from 'react';
import { TextField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { useAviStore } from './avi-store';

export function AviConversationPanel({ contextLabel }: { contextLabel?: string | null | undefined }): JSX.Element {
  const { t } = useI18n();
  const messages = useAviStore((state) => state.messages);
  const composer = useAviStore((state) => state.composer);
  const setComposer = useAviStore((state) => state.setComposer);
  const clearComposer = useAviStore((state) => state.clearComposer);
  const appendMessages = useAviStore((state) => state.appendMessages);

  const greeting = useMemo(() => t('avi.stub.greeting'), [t]);

  function handleCancel(): void {
    clearComposer();
  }

  function handleSend(): void {
    const text = composer.trim();
    if (!text) {
      return;
    }

    const createdAt = new Date().toISOString();
    const assistantReply = contextLabel ? t('avi.stub.replyWithContext', { context: contextLabel }) : t('avi.stub.reply');

    appendMessages([
      { id: crypto.randomUUID(), role: 'user', content: text, createdAt },
      { id: crypto.randomUUID(), role: 'assistant', content: assistantReply, createdAt }
    ]);

    clearComposer();
  }

  return (
    <>
      <div className="av-avi-panel__conversation">
        {messages.length === 0 ? <div className="av-avi-panel__bubble av-avi-panel__bubble--assistant">{greeting}</div> : null}
        {messages.map((message) => (
          <div
            key={message.id}
            className={[
              'av-avi-panel__bubble',
              message.role === 'assistant' ? 'av-avi-panel__bubble--assistant' : 'av-avi-panel__bubble--user'
            ].join(' ')}
          >
            {message.content}
          </div>
        ))}
      </div>

      <div className="av-avi-panel__composer">
        <TextField
          aria-label={t('avi.title')}
          placeholder={t('avi.stub.placeholder')}
          value={composer}
          onValueChange={setComposer}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              handleSend();
            }
          }}
        />
        <div className="av-avi-panel__actions">
          <button className="av-secondary-button" type="button" onClick={handleCancel} disabled={!composer.trim()}>
            {t('common.cancel')}
          </button>
          <button className="av-primary-button" type="button" onClick={handleSend} disabled={!composer.trim()}>
            <span>{t('common.send')}</span>
          </button>
        </div>
      </div>
    </>
  );
}
